'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Link, Unlink, CheckCircle, AlertCircle, ExternalLink, Webhook, CloudUpload, Save, Eye, EyeOff, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';

interface WebhookConfig {
  url: string;
  secret: string;
  name: string;
  enabled: boolean;
  lastTriggered?: string;
  triggerCount?: number;
}

interface DriveStatus {
  connected: boolean;
  folder: string;
  lastSync: string | null;
  fileCount: number;
}

const WEBHOOK_STORAGE_KEY = 'distortion_make_webhook';

function loadWebhookConfig(): WebhookConfig | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(WEBHOOK_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveWebhookConfig(config: WebhookConfig) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(WEBHOOK_STORAGE_KEY, JSON.stringify(config));
}

export default function ExportIntegrations() {
  const [showWebhookForm, setShowWebhookForm] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [savingWebhook, setSavingWebhook] = useState(false);

  // Webhook state
  const [webhookConfig, setWebhookConfig] = useState<WebhookConfig | null>(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [webhookName, setWebhookName] = useState('Make.com — Distortion Posts');

  // Google Drive status from reports table
  const [driveStatus, setDriveStatus] = useState<DriveStatus>({
    connected: false,
    folder: '/SEARCH4-DISTORTION/',
    lastSync: null,
    fileCount: 0,
  });
  const [loadingDrive, setLoadingDrive] = useState(true);

  const loadDriveStatus = useCallback(async () => {
    setLoadingDrive(true);
    const supabase = createClient();
    const { data, error } = await supabase
      .from('reports')
      .select('id, created_at')
      .order('created_at', { ascending: false })
      .limit(1);

    if (!error && data && data.length > 0) {
      const latest = new Date(data[0].created_at);
      const diffMs = Date.now() - latest.getTime();
      const diffH = Math.floor(diffMs / 3600000);
      const lastSync = diffH < 1 ? 'Vor weniger als 1 Std.' : `Vor ${diffH} Std.`;

      // Get total count
      const { count } = await supabase
        .from('reports')
        .select('id', { count: 'exact', head: true });

      setDriveStatus({
        connected: true,
        folder: '/SEARCH4-DISTORTION/',
        lastSync,
        fileCount: count ?? 0,
      });
    } else {
      setDriveStatus((prev) => ({ ...prev, connected: false, lastSync: null }));
    }
    setLoadingDrive(false);
  }, []);

  useEffect(() => {
    loadDriveStatus();
    const saved = loadWebhookConfig();
    if (saved) {
      setWebhookConfig(saved);
      setWebhookUrl(saved.url);
      setWebhookSecret(saved.secret);
      setWebhookName(saved.name);
    }
  }, [loadDriveStatus]);

  const handleSaveWebhook = () => {
    if (!webhookUrl.trim()) {
      toast.error('Bitte gib eine Webhook-URL ein');
      return;
    }
    if (!/^https?:\/\/.+/.test(webhookUrl.trim())) {
      toast.error('Ungültige URL — muss mit http:// oder https:// beginnen');
      return;
    }
    setSavingWebhook(true);
    setTimeout(() => {
      const config: WebhookConfig = {
        url: webhookUrl.trim(),
        secret: webhookSecret.trim(),
        name: webhookName.trim() || 'Make.com Webhook',
        enabled: true,
        lastTriggered: undefined,
        triggerCount: webhookConfig?.triggerCount ?? 0,
      };
      saveWebhookConfig(config);
      setWebhookConfig(config);
      setSavingWebhook(false);
      setShowWebhookForm(false);
      toast.success('Webhook gespeichert', {
        description: config.url.length > 50 ? config.url.slice(0, 50) + '…' : config.url,
      });
    }, 600);
  };

  const handleTestWebhook = async () => {
    if (!webhookConfig?.url) return;
    setTestingWebhook(true);
    try {
      const payload = {
        event: 'test',
        source: 'SEARCH4-DISTORTION',
        timestamp: new Date().toISOString(),
        data: {
          title: 'Test-Beitrag',
          platform: 'LinkedIn',
          caption: 'Dies ist ein Test-Webhook von SEARCH4-DISTORTION.',
        },
      };
      await fetch(webhookConfig.url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        mode: 'no-cors',
      });
      const updated: WebhookConfig = {
        ...webhookConfig,
        lastTriggered: new Date().toISOString(),
        triggerCount: (webhookConfig.triggerCount ?? 0) + 1,
      };
      saveWebhookConfig(updated);
      setWebhookConfig(updated);
      toast.success('Test-Webhook gesendet', {
        description: 'Prüfe dein Make.com-Szenario auf eingehende Daten.',
      });
    } catch {
      toast.error('Webhook-Test fehlgeschlagen');
    } finally {
      setTestingWebhook(false);
    }
  };

  const handleDeleteWebhook = () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(WEBHOOK_STORAGE_KEY);
    }
    setWebhookConfig(null);
    setWebhookUrl('');
    setWebhookSecret('');
    setWebhookName('Make.com — Distortion Posts');
    toast.success('Webhook entfernt');
  };

  const handleConnect = (id: string) => {
    setConnecting(id);
    setTimeout(() => {
      setConnecting(null);
      toast.success('Verbindung hergestellt');
    }, 1200);
  };

  const handleSync = (name: string) => {
    toast.success(`${name} wird synchronisiert…`);
    loadDriveStatus();
  };

  const formatLastTriggered = (iso?: string) => {
    if (!iso) return null;
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const diffM = Math.floor(diffMs / 60000);
    if (diffM < 1) return 'Gerade eben';
    if (diffM < 60) return `Vor ${diffM} Min.`;
    return `Vor ${Math.floor(diffM / 60)} Std.`;
  };

  return (
    <div className="space-y-4">
      {/* Social Platforms */}
      <div className="punk-card p-5">
        <p className="section-label mb-3">Social-Media-Kanäle</p>
        <div className="space-y-2">
          {[
            { id: 'int-li', name: 'LinkedIn', status: 'verbunden', detail: '4.2k Follower', lastSync: 'Vor 2 Std.', color: '#0A66C2' },
            { id: 'int-ig', name: 'Instagram', status: 'verbunden', detail: '1.8k Follower', lastSync: 'Vor 5 Std.', color: '#C13584' },
            { id: 'int-x', name: 'X (Twitter)', status: 'getrennt', detail: 'Nicht verbunden', color: '#ccc' },
            { id: 'int-fb', name: 'Facebook', status: 'fehler', detail: 'Token abgelaufen', lastSync: 'Vor 3 Tagen', color: '#1877F2' },
          ].map((int) => (
            <div key={int.id} className="flex items-start gap-3 p-3 border border-border rounded bg-surface hover:border-accent/30 transition-colors group">
              <div className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: `${int.color}20`, border: `1px solid ${int.color}40` }}>
                <span className="text-xs font-700" style={{ color: int.color }}>{int.name.slice(0, 1)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-600 text-white">{int.name}</span>
                  {int.status === 'verbunden' && <CheckCircle size={13} className="text-green-400" />}
                  {int.status === 'getrennt' && <Unlink size={13} className="text-muted" />}
                  {int.status === 'fehler' && <AlertCircle size={13} className="text-red-400" />}
                </div>
                <p className="text-xs text-muted truncate">{int.detail}</p>
                {int.lastSync && <p className="mono text-xs text-muted-2">{int.lastSync}</p>}
              </div>
              <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                {int.status === 'verbunden' ? (
                  <button onClick={() => handleSync(int.name)} className="p-1.5 text-muted hover:text-accent transition-colors" title="Synchronisieren">
                    <RefreshCw size={12} />
                  </button>
                ) : (
                  <button
                    onClick={() => handleConnect(int.id)}
                    disabled={connecting === int.id}
                    className="text-xs mono text-accent hover:text-white transition-colors px-2 py-1 border border-accent/40 rounded"
                  >
                    {connecting === int.id ? <RefreshCw size={11} className="animate-spin" /> : <Link size={11} />}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Google Drive */}
      <div className="punk-card p-5">
        <p className="section-label mb-3">Cloud-Speicher</p>
        <div className="p-3 border border-border rounded bg-surface hover:border-accent/30 transition-colors group">
          <div className="flex items-start gap-3">
            <div className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: '#34A85320', border: '1px solid #34A85340' }}>
              <span className="text-xs font-700" style={{ color: '#34A853' }}>G</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-600 text-white">Google Drive</span>
                {loadingDrive ? (
                  <RefreshCw size={13} className="text-muted animate-spin" />
                ) : driveStatus.connected ? (
                  <CheckCircle size={13} className="text-green-400" />
                ) : (
                  <Unlink size={13} className="text-muted" />
                )}
              </div>
              <p className="text-xs text-muted mono truncate">{driveStatus.folder}</p>
              {driveStatus.connected && driveStatus.lastSync && (
                <div className="flex items-center gap-2 mt-1">
                  <p className="mono text-xs text-muted-2">Zuletzt: {driveStatus.lastSync}</p>
                  {driveStatus.fileCount > 0 && (
                    <span className="mono text-xs text-muted">· {driveStatus.fileCount} Dateien</span>
                  )}
                </div>
              )}
              {!driveStatus.connected && !loadingDrive && (
                <p className="text-xs text-muted mt-1">Noch keine Beiträge synchronisiert</p>
              )}
            </div>
            <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
              {driveStatus.connected ? (
                <button onClick={() => handleSync('Google Drive')} className="p-1.5 text-muted hover:text-accent transition-colors" title="Synchronisieren">
                  <RefreshCw size={12} />
                </button>
              ) : (
                <button
                  onClick={() => handleConnect('int-gdrive')}
                  disabled={connecting === 'int-gdrive'}
                  className="text-xs mono text-accent hover:text-white transition-colors px-2 py-1 border border-accent/40 rounded"
                >
                  {connecting === 'int-gdrive' ? <RefreshCw size={11} className="animate-spin" /> : <Link size={11} />}
                </button>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={() => toast.info('Google Drive Ordner wird geöffnet…')}
          className="btn-ghost w-full justify-center mt-3 text-xs"
        >
          <ExternalLink size={12} />
          Drive-Ordner öffnen
        </button>
      </div>

      {/* Make.com Webhook */}
      <div className="punk-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Webhook size={14} className="text-accent" />
            <p className="section-label mb-0">Make.com Webhook</p>
          </div>
          <button
            onClick={() => setShowWebhookForm(!showWebhookForm)}
            className="text-xs mono text-accent hover:text-white transition-colors"
          >
            {showWebhookForm ? 'Abbrechen' : webhookConfig ? 'Bearbeiten' : 'Konfigurieren'}
          </button>
        </div>

        {/* Saved webhook status */}
        {webhookConfig && !showWebhookForm && (
          <div className="p-3 border border-green-800/40 rounded bg-green-950/20 mb-3">
            <div className="flex items-start gap-3">
              <div className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0" style={{ background: '#6D28D920', border: '1px solid #6D28D940' }}>
                <span className="text-xs font-700" style={{ color: '#8B5CF6' }}>M</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-600 text-white">{webhookConfig.name}</span>
                  <CheckCircle size={13} className="text-green-400" />
                </div>
                <p className="text-xs text-muted mono truncate">{webhookConfig.url.length > 45 ? webhookConfig.url.slice(0, 45) + '…' : webhookConfig.url}</p>
                {webhookConfig.lastTriggered && (
                  <p className="mono text-xs text-muted-2">Zuletzt ausgelöst: {formatLastTriggered(webhookConfig.lastTriggered)}</p>
                )}
                {webhookConfig.triggerCount !== undefined && webhookConfig.triggerCount > 0 && (
                  <p className="mono text-xs text-muted-2">{webhookConfig.triggerCount}× ausgelöst</p>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={handleTestWebhook}
                  disabled={testingWebhook}
                  className="p-1.5 text-muted hover:text-accent transition-colors"
                  title="Test senden"
                >
                  {testingWebhook ? <RefreshCw size={12} className="animate-spin" /> : <CloudUpload size={12} />}
                </button>
                <button
                  onClick={handleDeleteWebhook}
                  className="p-1.5 text-muted hover:text-red-400 transition-colors"
                  title="Webhook entfernen"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* No webhook configured */}
        {!webhookConfig && !showWebhookForm && (
          <div className="p-3 border border-border rounded bg-surface mb-3">
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded flex items-center justify-center" style={{ background: '#6D28D920', border: '1px solid #6D28D940' }}>
                <span className="text-xs font-700" style={{ color: '#8B5CF6' }}>M</span>
              </div>
              <div>
                <span className="text-xs font-600 text-white">Make.com</span>
                <p className="text-xs text-muted">Webhook konfigurieren</p>
              </div>
            </div>
          </div>
        )}

        {/* Webhook form */}
        {showWebhookForm && (
          <div className="mt-1 pt-3 border-t border-border space-y-3 fade-in">
            <div>
              <label className="label-punk" htmlFor="webhookName">Name</label>
              <input
                id="webhookName"
                className="input-punk text-xs"
                placeholder="Make.com — Distortion Posts"
                value={webhookName}
                onChange={(e) => setWebhookName(e.target.value)}
              />
            </div>
            <div>
              <label className="label-punk" htmlFor="webhookUrl">Endpunkt-URL *</label>
              <p className="text-xs text-muted-2 mb-1.5">Make.com oder Zapier Webhook-URL</p>
              <input
                id="webhookUrl"
                className="input-punk text-xs"
                placeholder="https://hook.eu1.make.com/abc123..."
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
              />
            </div>
            <div>
              <label className="label-punk" htmlFor="webhookSecret">Geheimschlüssel (optional)</label>
              <div className="relative">
                <input
                  id="webhookSecret"
                  className="input-punk text-xs pr-10"
                  placeholder="Webhook-Secret für Signaturprüfung"
                  type={showSecret ? 'text' : 'password'}
                  value={webhookSecret}
                  onChange={(e) => setWebhookSecret(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-white transition-colors"
                >
                  {showSecret ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
              </div>
            </div>
            <div className="p-3 bg-purple-950/20 border border-purple-800/30 rounded">
              <p className="text-xs text-purple-300 leading-relaxed">
                Der Webhook sendet POST-Anfragen mit Beitragsinhalt, Plattform und Metadaten aus der <span className="mono">reports</span>-Tabelle. Kompatibel mit Make.com und Zapier.
              </p>
            </div>
            <button
              onClick={handleSaveWebhook}
              disabled={savingWebhook}
              className="btn-primary w-full justify-center text-xs"
            >
              {savingWebhook ? (
                <>
                  <RefreshCw size={12} className="animate-spin" />
                  Speichern…
                </>
              ) : (
                <>
                  <Save size={12} />
                  Webhook speichern
                </>
              )}
            </button>
          </div>
        )}

        {/* Test button when webhook is saved and form is closed */}
        {webhookConfig && !showWebhookForm && (
          <button
            onClick={handleTestWebhook}
            disabled={testingWebhook}
            className="btn-ghost w-full justify-center text-xs mt-1"
          >
            {testingWebhook ? (
              <>
                <RefreshCw size={12} className="animate-spin" />
                Sende Test…
              </>
            ) : (
              <>
                <CloudUpload size={12} />
                Test-Webhook senden
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}